import { SkyBadge, SkyMessageBar, SkyRadioGroup, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import type { TransformationGraph } from "@shared/types/transformations";
import { testsEmptyMessage } from "@/lib/transformations/emptyResults";
import { CauseBlock, CauseNotClassified, failuresUnit } from "./CauseBlock";
import {
  formatSeconds,
  noTestResults,
  testCounts,
  testCountsLine,
  testGroupKey,
  testStatusPresentation,
} from "./transformFormatting";

/**
 * The last run's tests, grouped by the model they cover (canvas 16, Tests tab;
 * SEC-17).
 *
 * Grouping is by `dependsOn`, which is all the API knows: there are no tags on
 * tests, so no group label and no "expected to fail" badge come from the
 * platform. Intent lives in the transformation's name, never in a badge. A
 * failed test shows HOW MANY rows failed, never which.
 *
 * "No tests ran" and "the test results could not be read" are different
 * sentences: `artifacts.testResults` tells them apart.
 *
 * With a `scope` (the nodes one transformation selected, from the graph's
 * `selectedBy`), a sub-tab narrows the list to the tests on those nodes and is
 * the default; "All tests" is one selection away. Without one, nothing is
 * narrowed on a guess.
 *
 * The count column says "values" for accepted_values and unique tests, because dbt counts
 * distinct values there, not rows (canvas 18 finding 4); it agrees with the cause's chip.
 */

/** Statuses whose test carries a cause line (S7). */
const PROBLEM_STATUSES = new Set(["fail", "warn", "error", "runtime error"]);

function isProblem(status: string | null): boolean {
  return status !== null && PROBLEM_STATUSES.has(status);
}

export interface TestsPanelProps {
  graph: TransformationGraph;
  scope?: { name: string; nodeIds: ReadonlySet<string> };
}

type TestsScope = "mine" | "all";

/**
 * #1083: why the last run wrote no test results, when its row records it (`testsRequested`) and
 * nothing failed to read: Run tests off, the run stopped, the dbt step skipped. A real read
 * failure (a storage error) or a row from before the field returns null and keeps the warning.
 */
function knownNoTestsReason(graph: TransformationGraph): string | null {
  const run = graph.run;
  if (graph.artifacts.storageError || !run) return null;
  if (run.testsRequested === null || run.testsRequested === undefined) return null;
  return testsEmptyMessage(run.testsRequested, {
    runStatus: run.runStatus,
    transformStatus: run.stepStatus,
    skippedReason: run.skippedReason,
  });
}

export function TestsPanel({ graph, scope }: TestsPanelProps) {
  const [which, setWhich] = useState<TestsScope>("mine");
  const nodesById = useMemo(
    () => new Map(graph.nodes.map((node) => [node.uniqueId, node])),
    [graph.nodes],
  );
  const scoped = useMemo(
    () =>
      scope
        ? graph.tests.filter((test) => test.dependsOn.some((id) => scope.nodeIds.has(id)))
        : graph.tests,
    [graph.tests, scope],
  );
  const narrowed = scope !== undefined && which === "mine";
  const visible = narrowed ? scoped : graph.tests;

  const groups = useMemo(() => {
    const byGroup = new Map<string, TransformationGraph["tests"]>();
    for (const test of visible) {
      const key = testGroupKey(test, nodesById);
      byGroup.set(key, [...(byGroup.get(key) ?? []), test]);
    }
    return [...byGroup.entries()]
      .map(([key, tests]) => ({
        key,
        node: nodesById.get(key) ?? null,
        tests: [...tests].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => (a.node?.name ?? a.key).localeCompare(b.node?.name ?? b.key));
  }, [visible, nodesById]);

  const totals = useMemo(() => testCounts(visible), [visible]);

  if (graph.tests.length === 0) {
    const known = knownNoTestsReason(graph);
    if (graph.artifacts.testResults || known) {
      return (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="tests-panel-empty"
          title="No tests ran in the last run"
          message={
            known ??
            "Turn on Run tests for a dbt job and the results of dbt test appear here after the next run."
          }
        />
      );
    }
    return (
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        testId="tests-panel-unreadable"
        title="The last run's test results could not be read"
        message={
          graph.artifacts.storageError
            ? `The run's artifacts in storage could not be read (${graph.artifacts.storageError}). The counts on the header come from the step itself.`
            : "The run wrote no test results. Either tests were off for every dbt job, or the run stopped before dbt test."
        }
      />
    );
  }

  const testsWord = visible.length === 1 ? "test" : "tests";
  return (
    <div data-testid="tests-panel">
      {scope ? (
        // A radio group, not a second tab list inside the detail's tab panel: it filters the one
        // list below rather than switching panels (review N-8).
        <SkyRadioGroup
          className="mb-3"
          testId="tests-panel-scope"
          label="Show"
          layout="horizontal"
          name="tests-panel-scope"
          value={which}
          onChange={(value) => setWhich(value as TestsScope)}
          options={[
            { value: "mine", label: `This dbt job (${scoped.length})` },
            { value: "all", label: `All tests (${graph.tests.length})` },
          ]}
        />
      ) : null}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SkyText size={300} weight="semibold" testId="tests-panel-heading">
          {narrowed
            ? `${visible.length} ${testsWord} on the models ${scope.name} selected`
            : `All ${graph.tests.length} tests, grouped by model`}
        </SkyText>
        <span className="ml-auto flex items-center gap-1.5" data-testid="tests-panel-totals">
          {/* #1207 round 3: tests with no result never read "0 passed · 0 failed · 0 skipped". */}
          {noTestResults(totals) ? null : (
            <>
              <SkyBadge appearance="tint" color="success">
                {totals.ok} passed
              </SkyBadge>
              <SkyBadge appearance="tint" color={totals.failed > 0 ? "danger" : "subtle"}>
                {totals.failed} failed
              </SkyBadge>
              <SkyBadge appearance="tint" color="subtle">
                {totals.skipped} skipped
              </SkyBadge>
            </>
          )}
          {totals.notRun > 0 ? (
            <SkyBadge appearance="tint" color="subtle">
              {totals.notRun} did not run
            </SkyBadge>
          ) : null}
        </span>
      </div>

      {narrowed && visible.length === 0 ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="tests-panel-scope-empty"
          title="No test on this dbt job's models ran in the last run"
          message="Its models have no dbt tests, or no dbt job of the run had Run tests on. All tests lists every test of the run."
        />
      ) : null}

      {groups.map((group) => {
        // Counted by outcome, never "everything that did not fail": a model
        // whose tests were skipped after a failed dependency has not passed.
        const counts = testCounts(group.tests);
        return (
          <section
            key={group.key}
            className="mb-3 overflow-hidden rounded-md border border-border"
            aria-label={`Tests on ${group.node?.name ?? group.key}`}
            data-testid={`tests-group-${group.node?.name ?? "other"}`}
          >
            <div className="flex items-center gap-2 bg-muted px-3 py-2">
              <SkyText size={200} weight="semibold">
                {group.node?.name ?? group.key}
              </SkyText>
              {group.node?.materialized ? (
                <SkyText size={200} color="muted" font="monospace">
                  {group.node.materialized}
                </SkyText>
              ) : null}
              <SkyText size={200} color="muted" className="ml-auto" testId="tests-group-counts">
                {testCountsLine(counts)}
              </SkyText>
            </div>
            <ul className="divide-y divide-border">
              {group.tests.map((test) => {
                const presentation = testStatusPresentation(test.status);
                return (
                  <li
                    key={test.uniqueId}
                    className="grid grid-cols-[1fr_6rem_6rem_7rem] items-center gap-2 px-3 py-1.5"
                  >
                    <SkyText size={200} font="monospace" truncate block title={test.name}>
                      {test.name}
                    </SkyText>
                    <SkyBadge appearance="tint" color={presentation.color}>
                      {presentation.label}
                    </SkyBadge>
                    <SkyText
                      size={200}
                      font="numeric"
                      testId={`tests-panel-failures-${test.uniqueId}`}
                    >
                      {test.failures === null
                        ? "—"
                        : `${test.failures} ${failuresUnit(test, test.failures)}`}
                    </SkyText>
                    <SkyText size={200} font="numeric" color="muted">
                      {formatSeconds(test.executionTimeSeconds)}
                    </SkyText>
                    {test.cause || isProblem(test.status) ? (
                      // S7: the classified cause of a failed, warned or errored test, compact;
                      // the sentence is the server's, never composed here.
                      <div
                        className="col-span-4 pb-1"
                        data-testid={`tests-panel-cause-${test.uniqueId}`}
                      >
                        {test.cause ? (
                          <CauseBlock
                            whyOnlyWhenGeneric
                            cause={test.cause}
                            compact
                            testId={`tests-panel-cause-block-${test.uniqueId}`}
                          />
                        ) : (
                          <CauseNotClassified
                            testId={`tests-panel-cause-not-classified-${test.uniqueId}`}
                          />
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
